import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Popover, Spin, Tooltip } from 'antd';
import { CalendarOutlined, CarOutlined, HolderOutlined, InfoCircleOutlined, SwapOutlined } from '@ant-design/icons';
import { authService } from '../../../services/auth';
import { useFeedback } from '../../../utils/feedback';
import { getApiErrorMessage } from '../../../utils/apiError';
import { getTransportLabel } from '../../../utils/transportLabel';
import { updateOrder } from '../../orders/api/ordersApi';
import { canEditCustomerOrderForecastMonth } from '../../orders/customerOrderAccessPolicy';
import { fetchOrderDeliverySchedule, type OrderDeliveryScheduleTransport } from '../../orders/api/orderDeliveryScheduleApi';
import { CostPartialMark } from '../ReportTab';
import { monthLongName } from '../reportPeriod';
import { monthShortLabel } from '../incomeStatement/incomeStatement';
import { DashCard, money, percent } from './DashboardCharts';
import {
  buildForecastPlan,
  moveTargets,
  type ForecastPlanCard,
  type ForecastPlanColumn,
  type ForecastPlanMoves,
} from './forecastPlanModel';
import { dk, type DashboardViewContext } from './viewContext';
import './ForecastPlanBoard.css';

// „Prognoza miesiącami” on the Pulpit (owner 2026-10-29, decision
// 2026-10-30-przeciaganie-prognozy): ZK in the forecast per month of the
// page period. Dragging a card (or „Przenieś do miesiąca…”, for keyboard and
// touch) saves the ZK „Miesiąc prognozy” through the same `PUT /orders/:id`
// as the ZK card, then the page refetches the report: bars, tiles, „Skąd ta
// liczba” and the sales cut all read the new month. A truck part never moves
// here — its month is the truck's expected delivery (forecastPlanModel.ts).

const monthName = (month: string, language: string) => (
  `${monthLongName(Number(month.slice(5, 7)), language, false)} ${month.slice(0, 4)}`
);

// The trucks behind a TRUCK / MIXED card, read when the popover opens.
const TruckList: React.FC<{ ctx: DashboardViewContext; card: ForecastPlanCard }> = ({ ctx, card }) => {
  const { t } = ctx;
  const [state, setState] = useState<{ trucks: OrderDeliveryScheduleTransport[] | null; failed: boolean }>({ trucks: null, failed: false });
  React.useEffect(() => {
    let cancelled = false;
    fetchOrderDeliverySchedule(card.orderId)
      .then((schedule) => {
        if (cancelled) return;
        const inMonth = schedule.transports.filter((truck) => truck.forecastMonth === card.month);
        setState({ trucks: inMonth, failed: false });
      })
      .catch(() => { if (!cancelled) setState({ trucks: null, failed: true }); });
    return () => { cancelled = true; };
  }, [card.orderId, card.month]);
  if (state.failed) return <span className="mgmt-plan-trucks__note">{dk(t, 'plan.trucks.failed', { number: card.displayOrderNumber })}</span>;
  if (!state.trucks) return <Spin size="small" />;
  return (
    <div className="mgmt-plan-trucks" data-testid="mgmt-plan-trucks">
      {state.trucks.length === 0
        ? <span className="mgmt-plan-trucks__note">{dk(t, 'plan.trucks.none')}</span>
        : state.trucks.map((truck) => {
          const label = getTransportLabel(truck, dk(t, 'plan.trucks.unnamed'));
          return <Link key={truck.id} className="mgmt-plan-trucks__link" to={`/app/transports/${encodeURIComponent(truck.id)}`} title={label}>{label}</Link>;
        })}
      <span className="mgmt-plan-trucks__note">{dk(t, 'plan.trucks.hint')}</span>
    </div>
  );
};

const PlanCard: React.FC<{
  ctx: DashboardViewContext;
  card: ForecastPlanCard;
  canMove: boolean;
  targets: string[];
  onMove: (card: ForecastPlanCard, month: string) => void;
  onDragStart: (card: ForecastPlanCard) => void;
  onDragEnd: () => void;
  /** „Zaległe”: the card says its elapsed month. */
  showMonth?: boolean;
}> = ({ ctx, card, canMove, targets, onMove, onDragStart, onDragEnd, showMonth }) => {
  const { t, language } = ctx;
  const draggable = canMove && card.movable && !card.pending;
  const label = dk(t, 'orderLabel', { number: card.displayOrderNumber });
  const sourceText = [
    showMonth ? monthShortLabel(card.month, language) : null,
    dk(t, `plan.source.${card.source}`),
    card.share != null && card.share < 1 ? `${Math.round(card.share * 100)}%` : null,
  ].filter(Boolean).join(' · ');
  const trucks = card.source !== 'NO_TRUCK';
  return (
    <div
      className={`mgmt-plan-card is-${card.source.toLowerCase().replace('_', '-')}${draggable ? ' is-draggable' : ''}${card.pending ? ' is-pending' : ''}`}
      data-testid="mgmt-plan-card"
      data-order-id={card.orderId}
      data-source={card.source}
      draggable={draggable}
      aria-busy={card.pending || undefined}
      onDragStart={draggable ? (event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', card.orderId);
        onDragStart(card);
      } : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      title={dk(t, `plan.sourceHint.${card.source}`)}
    >
      <span className="mgmt-plan-card__head">
        {draggable && <HolderOutlined className="mgmt-plan-card__grip" aria-hidden />}
        {card.source === 'TRUCK' && <CarOutlined className="mgmt-plan-card__truck" aria-hidden />}
        <Link className="mgmt-plan-card__number" to={`/app/orders/${card.orderId}`} title={label} draggable={false}>{label}</Link>
      </span>
      <span className="mgmt-plan-card__customer" title={card.customerName}>{card.customerName}</span>
      <span className="mgmt-plan-card__money">
        <span className="mgmt-plan-card__revenue">{money(card.revenue)}</span>
        <span className="mgmt-plan-card__margin">
          {percent(card.marginPercent, language)}
          {card.costPending && card.marginPercent != null && <CostPartialMark hint={dk(t, 'notes.incompleteProfitHint')} testId="mgmt-plan-partial" />}
        </span>
      </span>
      <div className="mgmt-plan-card__foot">
        {card.pending ? (
          <span className="mgmt-plan-card__source is-pending">{dk(t, 'plan.pending')}</span>
        ) : trucks ? (
          <Popover
            trigger="click"
            destroyOnHidden
            title={dk(t, 'plan.trucks.title', { number: card.displayOrderNumber, month: monthName(card.month, language) })}
            content={<TruckList ctx={ctx} card={card} />}
          >
            <button type="button" className="mgmt-plan-card__source is-link" data-testid="mgmt-plan-trucks-open" title={dk(t, `plan.sourceHint.${card.source}`)}>
              <CarOutlined />
              <span>{sourceText}</span>
            </button>
          </Popover>
        ) : (
          <span className="mgmt-plan-card__source">{sourceText}</span>
        )}
        {canMove && card.movable && (
          <Dropdown
            trigger={['click']}
            disabled={card.pending}
            menu={{
              items: [{
                type: 'group',
                key: 'targets',
                label: dk(t, 'plan.moveTo'),
                children: targets.map((month) => ({ key: month, label: monthName(month, language) })),
              }],
              onClick: ({ key }) => onMove(card, String(key)),
            }}
          >
            <Button
              size="small"
              type="text"
              className="mgmt-plan-card__move"
              icon={<SwapOutlined />}
              aria-label={dk(t, 'plan.moveAria', { number: card.displayOrderNumber })}
              data-testid="mgmt-plan-move"
            />
          </Dropdown>
        )}
      </div>
    </div>
  );
};

// A long month shows its first cards; „+ N ZK” opens the whole column (no inner scroll).
const VISIBLE_CARDS = 8;

const ForecastPlanBoard: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, language } = ctx;
  const feedback = useFeedback();
  const [moves, setMoves] = useState<ForecastPlanMoves>({});
  const [dragging, setDragging] = useState<ForecastPlanCard | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const canMove = canEditCustomerOrderForecastMonth(authService.getCurrentUser());
  const rows = ctx.statement.forecastVsActual?.orders.forecast;
  const currentMonth = ctx.statement.currentMonth;
  const months = useMemo(() => ctx.periodMonths.map((month) => month.month), [ctx.periodMonths]);
  const plan = useMemo(() => buildForecastPlan(rows ?? [], months, currentMonth, moves), [rows, months, currentMonth, moves]);
  if (!rows) return null;

  const move = async (card: ForecastPlanCard, target: string) => {
    if (!card.movable || card.pending || target === card.month || target < currentMonth) return;
    const names = { number: card.displayOrderNumber, month: monthName(target, language) };
    setMoves((current) => ({ ...current, [card.orderId]: target }));
    try {
      await updateOrder(card.orderId, { forecastMonth: target });
    } catch (error) {
      setMoves(({ [card.orderId]: _dropped, ...rest }) => rest);
      feedback.error(getApiErrorMessage(error, t, 'reports:managementDashboard.plan.moveError', undefined, names));
      return;
    }
    const outside = months.includes(target) ? '' : ` ${dk(t, 'plan.outsidePeriod')}`;
    feedback.success(dk(t, card.source === 'MIXED' ? 'plan.movedMixed' : 'plan.moved', names) + outside);
    try {
      await ctx.reloadResult();
    } catch {
      feedback.warning(dk(t, 'plan.reloadFailed', names));
    } finally {
      setMoves(({ [card.orderId]: _done, ...rest }) => rest);
    }
  };

  // Only the current and later months take a drop; elapsed months never do.
  const accepts = (month: string) => dragging != null && dragging.month !== month && month >= currentMonth;
  const allPast = plan.columns.length === 0;
  const leadKey = allPast && !plan.overdue ? 'plan.leadPastEmpty'
    : !canMove ? 'plan.leadReadOnly' : allPast ? 'plan.leadPast' : 'plan.lead';
  const lead = dk(t, leadKey) + (ctx.scenario === 'base' ? '' : ` ${dk(t, 'plan.leadScenario')}`);
  const pastRange = plan.pastMonths.length === 0 ? '' : plan.pastMonths.length === 1
    ? monthName(plan.pastMonths[0], language)
    : `${monthLongName(Number(plan.pastMonths[0].slice(5, 7)), language, false)} – ${monthName(plan.pastMonths[plan.pastMonths.length - 1], language)}`;

  const renderCards = (column: ForecastPlanColumn, key: string) => {
    const open = expanded.has(key);
    const shown = open ? column.cards : column.cards.slice(0, VISIBLE_CARDS);
    const hidden = column.cards.length - shown.length;
    return (
      <div className="mgmt-plan__cards">
        {column.cards.length === 0 && <span className="mgmt-plan__empty">{dk(t, 'plan.empty')}</span>}
        {shown.map((card) => (
          <PlanCard
            key={card.key}
            ctx={ctx}
            card={card}
            canMove={canMove}
            targets={moveTargets(card, currentMonth, ctx.year)}
            onMove={(target, month) => { void move(target, month); }}
            onDragStart={setDragging}
            onDragEnd={() => { setDragging(null); setOver(null); }}
            showMonth={key === 'overdue'}
          />
        ))}
        {(hidden > 0 || (open && column.cards.length > VISIBLE_CARDS)) && (
          <Button
            size="small"
            type="text"
            className="mgmt-plan__more"
            data-testid="mgmt-plan-more"
            onClick={() => setExpanded((current) => {
              const next = new Set(current);
              if (open) next.delete(key); else next.add(key);
              return next;
            })}
          >
            {open ? dk(t, 'plan.less') : dk(t, 'plan.more', { count: hidden })}
          </Button>
        )}
      </div>
    );
  };

  const cells = plan.columns.length + (plan.overdue ? 1 : 0);

  return (
    <DashCard
      title={dk(t, 'plan.title', { period: ctx.periodLabel })}
      icon={<CalendarOutlined />}
      tone="indigo"
      lead={lead}
      className="mgmt-plan-card-section"
      testId="mgmt-plan"
      extra={(
        <Tooltip title={dk(t, 'plan.info')} placement="topRight">
          <InfoCircleOutlined className="mgmt-plan__info" aria-label={dk(t, 'plan.info')} />
        </Tooltip>
      )}
    >
      {plan.pastMonths.length > 0 && !plan.overdue && !allPast && (
        <p className="mgmt-plan__past" data-testid="mgmt-plan-past">{dk(t, 'plan.pastEmpty', { range: pastRange })}</p>
      )}
      {cells > 0 && (
        <div className={`mgmt-plan is-cols-${Math.min(6, cells)}`}>
          {plan.overdue && (
            <section
              className="mgmt-plan__month is-overdue"
              data-testid="mgmt-plan-overdue"
              data-revenue={plan.overdue.revenue}
              aria-label={dk(t, 'plan.overdue')}
            >
              <header className="mgmt-plan__head">
                <span className="mgmt-plan__name" title={dk(t, 'plan.overdueHint', { range: pastRange })}>{dk(t, 'plan.overdue')}</span>
                <span className="mgmt-plan__count">{dk(t, 'plan.count', { count: plan.overdue.cards.length })}</span>
                <strong className="mgmt-plan__sum" data-testid="mgmt-plan-sum" title={money(plan.overdue.revenue)}>{money(plan.overdue.revenue)}</strong>
              </header>
              {renderCards(plan.overdue, 'overdue')}
            </section>
          )}
          {plan.columns.map((column) => {
            const name = monthName(column.month, language);
            return (
              <section
                key={column.month}
                className={`mgmt-plan__month${over === column.month && accepts(column.month) ? ' is-over' : ''}${column.month === currentMonth ? ' is-current' : ''}`}
                data-testid={`mgmt-plan-month-${column.month}`}
                data-revenue={column.revenue}
                aria-label={name}
                onDragOver={(event) => {
                  if (!accepts(column.month)) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  if (over !== column.month) setOver(column.month);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver((current) => (current === column.month ? null : current));
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const card = dragging;
                  setOver(null);
                  setDragging(null);
                  if (card && accepts(column.month)) void move(card, column.month);
                }}
              >
                <header className="mgmt-plan__head">
                  <span className="mgmt-plan__name" title={name}>{monthLongName(Number(column.month.slice(5, 7)), language)}</span>
                  <span className="mgmt-plan__count">{dk(t, 'plan.count', { count: column.cards.length })}</span>
                  <strong className="mgmt-plan__sum" data-testid="mgmt-plan-sum" title={money(column.revenue)}>
                    {money(column.revenue)}
                  </strong>
                </header>
                {renderCards(column, column.month)}
              </section>
            );
          })}
        </div>
      )}
    </DashCard>
  );
};

export default ForecastPlanBoard;
